import { FieldValue, fieldControlClass, fieldDescribedBy } from "./FieldValue";
import { ColourFieldProps } from "./ColourField.types";

/** What a colour has to look like to be safe in an email's `style` attribute. */
export const HexColourPattern = "#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})";

// The native picker only takes "#rrggbb", so a short colour is widened and no colour is black.
const toPickerValue = (value: string | null | undefined) => {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value ?? "")?.[1];

  if (!hex) {
    return "#000000";
  }

  return `#${hex.length === 3 ? [...hex].map((digit) => digit + digit).join("") : hex}`.toLowerCase();
};

const isColour = (value: string | null | undefined) => new RegExp(`^${HexColourPattern}$`).test(value ?? "");

// A grey check where there is no colour, so empty reads as empty rather than as black.
const NoColour = "repeating-conic-gradient(#dee2e6 0% 25%, #fff 0% 50%) 50% / 0.75rem 0.75rem";

/** Hex colour editable field: the colour itself to press for the browser's picker, then a box to type into. */
export function ColourField({
  label,
  name,
  value,
  onChange,
  disabled,
  required,
  error,
  className,
  placeholder,
  helpText,
}: ColourFieldProps) {
  return (
    <FieldValue label={label} htmlFor={name} required={required} helpText={helpText} error={error} className={className}>
      <div className="input-group">
        <span
          className="input-group-text p-0 position-relative overflow-hidden flex-shrink-0"
          style={{ width: "2.5rem", background: isColour(value) ? value ?? undefined : NoColour }}
        >
          <input
            type="color"
            aria-label={`${label} picker`}
            className="position-absolute top-0 start-0 w-100 h-100 border-0 p-0 opacity-0"
            style={{ cursor: disabled ? "default" : "pointer" }}
            value={toPickerValue(value)}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
          />
        </span>
        <input
          id={name}
          aria-describedby={fieldDescribedBy(name, helpText, error)}
          aria-invalid={error ? true : undefined}
          name={name}
          type="text"
          className={fieldControlClass("form-control", error)}
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value.trim())}
          pattern={HexColourPattern}
          title="A hex colour, such as #1a2b3c"
          placeholder={placeholder}
          disabled={disabled}
          required={required}
        />
      </div>
    </FieldValue>
  );
}
